import { displayTime } from './replay';
import type { BufferedRange } from './session-replay';

export const playbackRates = [.5, 1, 2, 4, 8, 16] as const;
export interface PlaybackClock { headMs: number; sampledAtMs: number; playing: boolean; rate: number }
export interface PlaybackWindow {
  durationMs: number;
  startMs?: number;
  delaySeconds?: number;
  ranges?: BufferedRange[];
  suspended?: boolean;
}

function limits(window: PlaybackWindow) {
  const duration = Math.max(0, window.durationMs);
  const delay = Math.max(0, Math.min(120, window.delaySeconds ?? 0)) * 1000;
  return { duration, delay, start: Math.max(0, Math.min(duration, window.startMs ?? 0)) };
}

export function createPlaybackClock(atMs: number, now: number, window: PlaybackWindow, playing = true): PlaybackClock {
  return seekPlaybackClock({ headMs: 0, sampledAtMs: now, playing, rate: 1 }, atMs, now, window, playing);
}

/** Tick always consumes elapsed wall time, including time spent paused or waiting for a chunk. */
export function tickPlaybackClock(clock: PlaybackClock, now: number, window: PlaybackWindow): PlaybackClock {
  const { duration, delay } = limits(window);
  const head = Math.max(0, Math.min(duration + delay, clock.headMs));
  const atMs = displayTime(head, delay / 1000);
  let availableUntil = duration;
  if (window.ranges) {
    const containing = window.ranges.find(range => atMs >= range.startMs && (atMs < range.endMs || atMs === duration && range.endMs === duration));
    availableUntil = containing?.endMs ?? atMs;
    if (containing) {
      for (const range of [...window.ranges].sort((a, b) => a.startMs - b.startMs)) {
        if (range.startMs <= availableUntil && range.endMs > availableUntil) availableUntil = range.endMs;
      }
    }
  }
  const elapsed = window.suspended || !clock.playing ? 0 : Math.max(0, now - clock.sampledAtMs);
  const headMs = Math.min(duration + delay, Math.max(head, Math.min(head + elapsed * clock.rate, availableUntil + delay)));
  return { ...clock, headMs, sampledAtMs: now, playing: clock.playing && headMs < duration + delay };
}

/** Explicit navigation starts a new time anchor and pauses unless playback was requested. */
export function seekPlaybackClock(clock: PlaybackClock, atMs: number, now: number, window: PlaybackWindow, playing = false): PlaybackClock {
  const { duration, delay, start } = limits(window);
  const position = Math.max(start, Math.min(duration, Number.isFinite(atMs) ? atMs : start));
  return { ...clock, headMs: position + delay, sampledAtMs: now, playing: playing && position < duration };
}

export function changePlaybackRate(clock: PlaybackClock, rate: number, now: number, window: PlaybackWindow): PlaybackClock {
  const settled = tickPlaybackClock(clock, now, window);
  return playbackRates.some(value => value === rate) ? { ...settled, rate } : settled;
}

export function pausePlaybackClock(clock: PlaybackClock, now: number, window: PlaybackWindow): PlaybackClock {
  return { ...tickPlaybackClock(clock, now, window), playing: false };
}

export function resumePlaybackClock(clock: PlaybackClock, now: number, window: PlaybackWindow): PlaybackClock {
  const { duration, delay, start } = limits(window);
  const atMs = displayTime(clock.headMs, delay / 1000);
  return seekPlaybackClock(clock, atMs >= duration ? start : atMs, now, window, true);
}
